import {
  Injectable,
  ApplicationRef,
  ComponentRef,
  createComponent,
  EmbeddedViewRef,
  EnvironmentInjector,
  Type,
  signal,
  computed,
  inject
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { DynamicDialogRef } from './dynamic-dialog-ref';
import { DynamicDialogComponent } from './dynamic-dialog.component';
import type { DynamicDialogConfig, DynamicDialogState } from './dynamic-dialog.types';

/**
 * Dynamic Dialog Service - Razor UI Design System
 * Drop-in replacement for legacy DynamicDialogService
 *
 * @example
 * // In component
 * private dialogService = inject(DynamicDynamicDialogService);
 *
 * openDialog() {
 *   const ref = this.dialogService.open(MyDialogComponent, {
 *     header: 'My Dialog',
 *     width: '500px',
 *     data: { myData: 'value' }
 *   });
 *
 *   ref.onClose.subscribe(result => {
 *     this.logger.debug('Dialog closed', { result });
 *   });
 * }
 */
@Injectable({
  providedIn: 'root'
})
export class DynamicDynamicDialogService {
  private readonly document = inject(DOCUMENT);
  private _dialogs = signal<DynamicDialogState[]>([]);
  private _dialogRefs = new Map<string, DynamicDialogRef>();
  private _dialogComponents = new Map<string, ComponentRef<DynamicDialogComponent>>();
  private _idCounter = 0;
  /**
   * RF-QA-196 · Page scroll lock while any dynamic dialog is open. A
   * service-owned CLASS on <body> (styles/_utilities.scss) rather than a
   * saved/restored inline `overflow`: the app shell scrolls inside
   * `main#main-content` (an h-screen layout), which an inline body style never
   * locked, and `ui-modal` removes body `overflow` unconditionally, which a
   * save/restore scheme would then resurrect as a stuck `hidden`.
   */
  static readonly SCROLL_LOCK_CLASS = 'rz-dialog-scroll-lock';

  /** List of active dialogs */
  readonly dialogs = computed(() => this._dialogs());

  /** Number of open dialogs */
  readonly dialogCount = computed(() => this._dialogs().length);

  constructor(
    private appRef: ApplicationRef,
    private environmentInjector: EnvironmentInjector
  ) {}

  /**
   * Open a dynamic dialog
   * @param component The component to render in the dialog
   * @param config Dialog configuration
   * @returns Reference to the opened dialog
   */
  open<T = any>(component: Type<unknown>, config: DynamicDialogConfig = {}): DynamicDialogRef<T> {
    const id = `dialog-${++this._idCounter}`;
    const ref = new DynamicDialogRef<T>();

    // Store the reference
    this._dialogRefs.set(id, ref);

    // Create dialog state
    const state: DynamicDialogState = {
      id,
      component,
      config: {
        modal: true,
        closable: true,
        closeOnEscape: true,
        dismissableMask: false,
        showHeader: true,
        position: 'center',
        width: '50vw',
        ...config
      },
      visible: true
    };

    // Add to dialogs list
    this._dialogs.update(dialogs => [...dialogs, state]);

    // RF-QA-196: a modal dialog locks page scroll for as long as any is open.
    this.lockBodyScroll();

    // Create and attach the dialog component
    this.createDialogComponent(id, state, ref);

    // Handle close
    ref.onClose.subscribe(() => {
      this.removeDialog(id);
    });

    return ref;
  }

  /**
   * Close all open dialogs
   */
  closeAll(): void {
    const ids = [...this._dialogRefs.keys()];
    ids.forEach(id => {
      const ref = this._dialogRefs.get(id);
      if (ref) {
        ref.close();
      }
    });
  }

  /**
   * Get dialog reference by ID
   */
  getDialogRef(id: string): DynamicDialogRef | undefined {
    return this._dialogRefs.get(id);
  }

  private createDialogComponent(id: string, state: DynamicDialogState, ref: DynamicDialogRef): void {
    // Create the dialog wrapper component
    const componentRef = createComponent(DynamicDialogComponent, {
      environmentInjector: this.environmentInjector
    });

    // Set inputs via componentRef.setInput() — required for signal-based inputs.
    componentRef.setInput('dialogId', id);
    componentRef.setInput('contentComponent', state.component);
    componentRef.setInput('config', state.config);
    componentRef.setInput('dialogRef', ref);

    // Attach to application
    this.appRef.attachView(componentRef.hostView);

    // Add to DOM
    const domElement = (componentRef.hostView as EmbeddedViewRef<unknown>).rootNodes[0];
    this.document.body.appendChild(domElement);

    // Store reference
    this._dialogComponents.set(id, componentRef);
  }

  private removeDialog(id: string): void {
    // Remove from dialogs list
    this._dialogs.update(dialogs => dialogs.filter(d => d.id !== id));

    // Clean up refs
    this._dialogRefs.delete(id);

    // Destroy component
    const componentRef = this._dialogComponents.get(id);
    if (componentRef) {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
      // The host was manually appended to body, outside an Angular container.
      // Destroying its view alone can leave an inert modal/backdrop over the app.
      componentRef.location.nativeElement.remove();
      this._dialogComponents.delete(id);
    }

    // RF-QA-196: release the scroll-lock once the last dialog is gone
    // (close, cancel, Escape and navigation teardown all end here).
    if (this._dialogs().length === 0) {
      this.unlockBodyScroll();
    }
  }

  /** RF-QA-196 · Idempotent (classList). Held until the last dialog closes. */
  private lockBodyScroll(): void {
    this.document.body.classList.add(DynamicDynamicDialogService.SCROLL_LOCK_CLASS);
  }

  /** RF-QA-196 · Releases the page scroll lock. */
  private unlockBodyScroll(): void {
    this.document.body.classList.remove(DynamicDynamicDialogService.SCROLL_LOCK_CLASS);
  }
}

// Export alias for backward compatibility
export { DynamicDynamicDialogService as DynamicDialogService };
